<script setup>
import { faqList } from '@/client'
import { toUnderscore, useTextStore } from '@/stores/texts'
import { nextTick } from 'vue'
import { watch } from 'vue'
import { inject } from 'vue'
import { ref } from 'vue'
import { client } from '@/lohnrechner'

const faqEntries = ref([])
const categories = ref([])
const categorySlugs = []

const activeEntry = ref(null)
const filter = ref(null)

const languageStore = useTextStore()

let langauge = null

function fetchEntries() {
  categories.value = []
  categorySlugs.length = 0
  faqEntries.value = []

  langauge = languageStore.language
  faqList({ query: { language: toUnderscore(languageStore.language) }, client }).then(
    (response) => {
      response.data.results.forEach((entry) => {
        entry.categories.forEach((category) => {
          if (categorySlugs.includes(category.slug)) return
          categories.value.push(category)
          categorySlugs.push(category.slug)
        })

        faqEntries.value.push(entry)
      })
    }
  )
}

watch(
  () => activeEntry.value,
  () => {
    if (activeEntry.value) {
      nextTick().then(() => {
        window.scrollTo({
          top: document.querySelector('.entry .active').offsetTop - 150,
          behavior: 'smooth'
        })
      })
    }
  }
)

fetchEntries()

languageStore.$subscribe((mutation, state) => {
  if (langauge !== state.language) {
    fetchEntries()
  }
})

function filterEntries(category) {
  if (filter.value === category) {
    clearFilter()
  } else {
    filter.value = category
  }
}

function clearFilter() {
  filter.value = null
}

function getEntries() {
  if (filter.value) {
    return faqEntries.value.filter((entry) =>
      entry.categories.find((category) => category.slug === filter.value.slug)
    )
  }

  return faqEntries.value
}

const t = inject('t')

const setDocTitle = inject('setTitle')
setDocTitle(t('faq'))
</script>
<template>
  <h1>FAQs</h1>

  <section class="buttons">
    <button
      v-for="category in categories"
      @click="filterEntries(category)"
      :class="{ active: filter === category }"
      :key="category.slug"
    >
      {{ category.name }}
    </button>
  </section>

  <div class="accordeons">
    <div v-for="entry in getEntries()" :key="entry.id" class="entry">
      <h3
        :class="{ active: activeEntry == entry.id }"
        @click="activeEntry == entry.id ? (activeEntry = null) : (activeEntry = entry.id)"
      >
        <span>
          {{ entry.title }}
        </span>
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21.5332 10.0635L11.1754 20.4216L0.817292 10.0635"
            stroke="#34333C"
            stroke-width="1.2"
          />
          <line
            x1="11.1422"
            y1="19.7344"
            x2="11.1422"
            y2="0.946712"
            stroke="#34333C"
            stroke-width="1.2"
          />
        </svg>
      </h3>
      <div
        v-html="entry.content"
        class="content"
        :class="{ hidden: activeEntry != entry.id }"
      ></div>
    </div>
  </div>
</template>
<style>
.entry .content > div:first-child {
  margin-top: 20px;
}
</style>

<style scoped>
.buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
}

.entry .content.hidden {
  position: absolute;
  opacity: 0;
  transform: scaleY(0);
  transform-origin: top;
  color: transparent;
}

.entry {
  overflow: hidden;
  transition: all 200ms ease-in-out;
}

.entry .content {
  transition: transform 200ms ease-in-out;
  max-height: 2000px;
  overflow: hidden;
}

button {
  border: solid 1px var(--color-signal);
  background-color: white;
  color: var(--color-signal);
  border-radius: 30px;
  height: 40px;
  width: 200px;
  font-weight: 700;
  transition: all 200ms;
  cursor: pointer;
  font-size: 17px;
}

button.active {
  background-color: var(--color-signal);
  color: white;
}

h3 {
  font-size: var(--font-size);
  font-weight: 400;
  margin: 0;
  cursor: pointer;
  transition: all 200ms;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

h3 svg {
  transition: all 200ms;
  flex: 0 0 auto;
}

h3.active svg {
  transform: rotate(180deg);
}

h3.active {
  font-weight: 700;
}

.entry {
  background-color: #f9f1f1;
  border-bottom: solid 1px var(--color-text);
  padding: 30px 60px;
}

.entry:hover {
  background-color: #e6d9d9;
}

.entry:first-child {
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
}

.entry:last-child {
  border-bottom-left-radius: 30px;
  border-bottom-right-radius: 30px;
  border-bottom: none;
}

.accordeons {
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;
}
</style>
